import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';

import authenticationService from '../services/authentication/AuthenticationService';
import history from '../history/history';

import {captureError} from './log';
import config from '../../config';

const HEADER_TRACE_ID = config?.monitoring?.header || 'X-OnceForAll-TraceId';

const generateTraceId = () => {
  const appId = config?.monitoring?.appId || 'frontend';
  const timestamp = Date.now();
  const randSuffix = Math.floor(Math.random() * 100000);

  return `${appId}-${timestamp}-${randSuffix}`;
};

function handleError(response, redirectOnAccessDenied = true) {
  if (redirectOnAccessDenied && response.status === 403) {
    captureError('Access Denied redirection due to 403 error');
    history.push('/access-denied');
  }

  const error = new Error(`${response.status} ${response.statusText}`);
  error.response = response;
  throw error;
}

export function checkStatus(response, redirectOnAccessDenied = true) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  return handleError(response, redirectOnAccessDenied);
}

export function checkStatusExceptNotFound(response) {
  if (response.status >= 200 && response.status < 300) {
    return response.json();
  }

  if (response.status === 404) {
    return null;
  }

  return handleError(response);
}

export function parseHeadersAndJson(response) {
  return response.json()
    .then(json => ({
      json,
      headers: response.headers
    }));
}

export function parseHeadersAndBlob(response) {
  return response.blob()
    .then(blob => ({
      blob,
      headers: response.headers
    }));
}

// Update both Keycloak and roles tokens, then call the url with the required tokens
export function authFetch(url, definedOptions = {}, withoutRoles = false) {
  const options = definedOptions || {};

  return authenticationService.updateTokenKeycloak()
    .then(tokenKeycloak => {
      options.headers = options.headers || {};
      options.headers[HEADER_TRACE_ID] = generateTraceId();
      options.headers.Authorization = `Bearer ${tokenKeycloak}`;
      if (withoutRoles) {
        return fetch(url, options);
      }

      return authenticationService.updateTokenRoles().then(tokenRoles => {
        options.headers.Permission = tokenRoles;
        return fetch(url, options);
      });
    });
}

export function fetchRetry(fetchFn, retryCodes, acceptEmpty = true, waitTime = 300, retryTimes = 5) {
  return new Promise((resolve, reject) => {
    function retry(incrementalWaitTime, decrementalRetryTimes) {
      fetchFn()
        .then(response => checkStatus(response, false))
        .then(response => response.json())
        .then(response => {
          if (!isEmpty(response) || acceptEmpty) {
            resolve(response);
          } else {
            throw new Error('response is empty');
          }
        })
        .catch(error => {
          if (
            decrementalRetryTimes > 0 &&
            (
              retryCodes.includes(error.response?.status) ||
              (!acceptEmpty && error.message === 'response is empty')
            )
          ) {
            setTimeout(() => retry(incrementalWaitTime * 2, decrementalRetryTimes - 1), incrementalWaitTime);
          } else {
            reject(error);
          }
        });
    }

    retry(waitTime, retryTimes);
  });
}

export function fetchJsonFile(jsonUrl) {
  function loadJSON(url, callback) {
    const xmlHttpRequest = new XMLHttpRequest();
    xmlHttpRequest.overrideMimeType('application/json');
    xmlHttpRequest.open('GET', url, true);
    xmlHttpRequest.setRequestHeader('cache-control', 'no-cache, must-revalidate, post-check=0, pre-check=0');
    xmlHttpRequest.setRequestHeader('cache-control', 'max-age=0');
    xmlHttpRequest.setRequestHeader('expires', '0');
    xmlHttpRequest.setRequestHeader('expires', 'Tue, 01 Jan 1980 1:00:00 GMT');
    xmlHttpRequest.setRequestHeader('pragma', 'no-cache');
    xmlHttpRequest.onreadystatechange = () => {
      if (xmlHttpRequest.readyState === 4 && xmlHttpRequest.status === 200) {
        // Required use of an anonymous callback as .open will NOT return a value but simply returns undefined in asynchronous mode
        callback(xmlHttpRequest.responseText);
      }
    };

    xmlHttpRequest.send(null);
  }

  return new Promise((resolve, reject) => {
    loadJSON(jsonUrl, response => {
      if (response) {
        try {
          resolve(JSON.parse(response));
        } catch (error) {
          reject(error);
        }
      }
    });
  });
}

// Open file url and handle special file prompt for IE/Edge
export function openOrSaveFile(fileUrl) {
  // Condition to detect if browser is IE/Edge or not by testing if proprietary method msSaveOrOpenBlob exists
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    fetch(fileUrl)
      .then(checkStatus)
      .then(parseHeadersAndBlob)
      .then(({blob, headers}) => {
        let filename = 'filename';
        try {
          filename = headers.get('Content-Disposition').split(';')[1].trim().split('=')[1].replace(/"/g, '');
        } catch (error) {
          captureError(`Error while getting the file name from Content-disposition: (CD = ${headers.get('Content-Disposition')}, Blob = ${blob})`, error);
        }

        try {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } catch (error) {
          captureError('Error while open download blox on IE/Edge', error);
        }
      });
  } else {
    window.open(fileUrl);
  }
}

// Export functions for mock purposes
const exportFunctions = {
  checkStatus,
  parseHeadersAndJson,
  parseHeadersAndBlob,
  authFetch,
  fetchRetry,
  openOrSaveFile
};

export default exportFunctions;
